import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

type Step = 'email' | 'otp';

export function Login() {
    const { requestOTP, login } = useAuth();
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    async function handleEmailSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const result = await requestOTP(email);

            if (result.success) {
                setMessage('Check your inbox');
                setStep('otp');
            } else {
                setError(result.error || 'Access denied');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Something went wrong');
        } finally {
            setLoading(false);
        }
    }

    async function handleOTPSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await login(email, code);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid code');
        } finally {
            setLoading(false);
        }
    }

    function handleBack() {
        setStep('email');
        setCode('');
        setError(null);
        setMessage(null);
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#050508] relative overflow-hidden">
            {/* Subtle animated background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            </div>

            <div className="w-full max-w-sm relative z-10 px-6">
                {/* Logo - minimal */}
                <div className="text-center mb-12">
                    <div className="text-5xl mb-6 opacity-80">🐋</div>
                    <h1 className="text-2xl font-semibold tracking-tight text-white/90">
                        Watching Whales
                    </h1>
                </div>

                <Card className="border-white/5 bg-white/[0.02] backdrop-blur-sm">
                    <CardContent className="pt-8 pb-8">
                        {error && (
                            <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                                {error}
                            </div>
                        )}

                        {step === 'email' ? (
                            <form onSubmit={handleEmailSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                        disabled={loading}
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 text-center"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-white/10 hover:bg-white/15 text-white border-0"
                                    disabled={loading || !email}
                                >
                                    {loading ? '...' : 'Continue'}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleOTPSubmit} className="space-y-6">
                                {message && (
                                    <p className="text-center text-white/40 text-sm">{message}</p>
                                )}

                                <div className="space-y-2">
                                    <Input
                                        id="code"
                                        type="text"
                                        placeholder="000000"
                                        value={code}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setCode(val);
                                        }}
                                        required
                                        autoFocus
                                        disabled={loading}
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-14 text-center text-2xl tracking-[0.4em] font-mono"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-white/10 hover:bg-white/15 text-white border-0"
                                    disabled={loading || code.length !== 6}
                                >
                                    {loading ? '...' : 'Enter'}
                                </Button>

                                <button
                                    type="button"
                                    onClick={handleBack}
                                    disabled={loading}
                                    className="w-full text-sm text-white/30 hover:text-white/50 transition-colors"
                                >
                                    ←
                                </button>
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Minimal footer */}
                <p className="text-center text-white/15 text-xs mt-8">
                    Invite only
                </p>
            </div>
        </div>
    );
}
